import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Lock as LockIcon,
  AttachMoney as MoneyIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { purchaseServiceAccess, ServiceAccessRequest } from '../lib/api/projectAccess';
import { getUserCredits } from '../lib/api/promotionBids';

interface ServiceAccessModalProps {
  open: boolean;
  onClose: () => void;
  serviceId: string;
  serviceTitle: string;
  onAccessGranted: () => void;
}

const ServiceAccessModal: React.FC<ServiceAccessModalProps> = ({
  open,
  onClose,
  serviceId,
  serviceTitle,
  onAccessGranted
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userCredits, setUserCredits] = useState(0);
  
  const ACCESS_COST = 2; // 2 credits to access service provider details

  // Load user credits when modal opens
  useEffect(() => {
    if (open) {
      loadUserCredits();
    }
  }, [open]);

  const loadUserCredits = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const credits = await getUserCredits();
      setUserCredits(credits);
    } catch (err: any) {
      setError(err.message || 'Грешка при зареждане на кредитите');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (userCredits < ACCESS_COST) {
      setError(`Нямате достатъчно кредити. Нужни: ${ACCESS_COST}, налични: ${userCredits}`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const request: ServiceAccessRequest = {
        service_id: serviceId,
        credits_spent: ACCESS_COST
      };

      await purchaseServiceAccess(request);
      
      setSuccess(true);
      onAccessGranted();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 2000);
      
    } catch (err: any) {
      setError(err.message || 'Грешка при закупуване на достъпа');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a2e',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
        }
      }}
    >
      <DialogTitle sx={{ color: '#ffffff', textAlign: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <LockIcon sx={{ color: '#28A0F0', fontSize: '2rem' }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Достъп за контакт с изпълнителя
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>
        {success ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon sx={{ color: '#10b981', fontSize: '4rem', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#10b981', mb: 1 }}>
              Успешно закупен достъп!
            </Typography>
            <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
              Сега можете да се свържете с изпълнителя чрез имейл.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={3}>
            <Alert 
              severity="info" 
              sx={{ 
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
                border: '1px solid rgba(40, 160, 240, 0.3)',
                color: '#ffffff'
              }}
            >
              <Typography variant="body2">
                За да се свържете с изпълнителя, трябва да закупите достъп за <strong>{ACCESS_COST} кредита</strong>.
              </Typography>
            </Alert>

            <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#ffffff', mb: 2 }}>
                  {serviceTitle}
                </Typography>
                
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <VisibilityIcon sx={{ color: '#28A0F0' }} />
                  <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                    Ще получите достъп до:
                  </Typography>
                </Box>
                
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  <li>
                    <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 0.5 }}>
                      Възможност за изпращане на имейл до изпълнителя
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" sx={{ color: '#a0a0a0', mb: 0.5 }}>
                      Пряка комуникация за обсъждане на проекта
                    </Typography>
                  </li>
                </Box>
              </CardContent>
            </Card>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress sx={{ color: '#28A0F0' }} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon sx={{ color: '#28A0F0' }} />
                  <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                    Ваши кредити:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    {userCredits}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#28A0F0', fontWeight: 600 }}>
                  -{ACCESS_COST} кредита
                </Typography>
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                {error}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={onClose}
            sx={{ 
              color: '#a0a0a0',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Отказ
          </Button>
          <Button
            onClick={handlePurchase}
            disabled={loading || submitting || userCredits < ACCESS_COST}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
              color: '#ffffff',
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                color: '#a0a0a0'
              }
            }}
          >
            {submitting ? 'Закупуване...' : `Закупи достъп (${ACCESS_COST} кредита)`}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ServiceAccessModal;
